import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  PagesCreateNewHandbookPageDocument,
  PagesFolderInformationDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { DeletePageDialog } from 'modules/pages/components/DeletePageDialog'
import { MovePageModal } from 'modules/pages/components/MovePageModal'
import { UploadFilesModal } from 'modules/pages/components/UploadFilesModal'
import { usePagesContext } from 'modules/pages/lib/hooks/usePagesContext'

import { FolderEmptyState } from './components/FolderEmptyState'
import { PagesList } from './components/PagesList'

const Folder = () => {
  const { canEditPages } = usePagesContext()
  const { employee: owner } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [openDeleteDialog, setOpenDeleteDialog] = useState<number | null>(null)
  const [openMovePageModal, setOpenMovePageModal] = useState<number | null>(null)
  const [openUploadFilesModal, setOpenUploadFilesModal] = useState(false)
  const { folderSlug } = useParams<{ folderSlug: string }>()

  const [createPage] = useMutation(PagesCreateNewHandbookPageDocument, {
    extractorFn: (data) => data.handbookPagesMutations.createHandbookpagesPage,
    refetchQueries: [PagesFolderInformationDocument],
  })

  const { loading, error, data } = useQuery(PagesFolderInformationDocument, {
    variables: {
      slug: String(folderSlug),
    },
    skip: !folderSlug,
  })

  const folder = data?.handbookPages.foldersConnection.nodes[0]

  useEffect(() => {
    if (!loading && !error && folderSlug) {
      track(trackingEvents.pages.PagesFolderViewed, {
        folder_type: folderSlug,
      })
    }
  }, [loading, error, folderSlug])

  const handleCreatePage = async () => {
    if (!folderSlug || !canEditPages) return

    const variables = {
      title: i18n.t('pages.create_page.title'),
      ownerId: Number(owner?.id),
      folderType: folderSlug,
    }

    const result = await createPage({
      variables,
    })

    const handler = handleResult(result, () => {})
    const pageId = handler?.success ? handler.data?.page?.id : undefined

    if (pageId) {
      track(trackingEvents.pages.PagesCreateNewPage, {
        folder_type: folderSlug,
        page_id: String(pageId),
      })
      navigate(
        navigation.pages.folder.page.fullPath({
          folderSlug,
          pageId,
        })
      )
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }

    return handler
  }

  return (
    <ResponsivePage.Body error={error || !folder} loading={loading || !folderSlug}>
      {folder?.numberOfItems === 0 ? (
        <FolderEmptyState
          handleCreatePage={handleCreatePage}
          handleImportPDF={() => setOpenUploadFilesModal(true)}
        />
      ) : (
        folderSlug && (
          <PagesList
            handleCreatePage={handleCreatePage}
            setOpenDeleteDialog={setOpenDeleteDialog}
            setOpenMovePageModal={setOpenMovePageModal}
            folderSlug={folderSlug}
            setOpenUploadFilesModal={setOpenUploadFilesModal}
          />
        )
      )}

      {openDeleteDialog && (
        <DeletePageDialog pageId={openDeleteDialog} onClose={() => setOpenDeleteDialog(null)} />
      )}

      {openMovePageModal && folderSlug && (
        <MovePageModal
          pageId={openMovePageModal}
          initialFolderType={folderSlug}
          onClose={() => setOpenMovePageModal(null)}
        />
      )}

      {openUploadFilesModal && (
        <UploadFilesModal
          isOpen={openUploadFilesModal}
          onClose={() => setOpenUploadFilesModal(false)}
          folderType={folderSlug}
        />
      )}
    </ResponsivePage.Body>
  )
}
export default Folder
